import { Inject, Injectable } from '@angular/core';
import { DtrumApi } from '@dynatrace/dtrum-api-types';
import { buildEnvironment } from '@rci/build-env';
import { LogService } from '@rci/log-service';
import { SESSION_STORAGE } from '@rci/storage/session-storage.service';
import { WindowService } from '@rogers/cdk/common/services';

declare const dtrum: DtrumApi;

interface UserMonitoringCommonProperties {
  env: string;
  service: string;
  version: string;
}

export interface UserMonitoringConfiguration {
  env: string;
  isEnabled: boolean;
  service: string;
  version: string;
}

@Injectable({ providedIn: 'root' })
export class UserMonitoringServiceConfig {
  userMonitoring: UserMonitoringConfiguration = {
    isEnabled: false,
    env: undefined,
    service: undefined,
    version: undefined,
  };
}

/* Abstract the dynatrace RUM api in a generic user monitoring service */
@Injectable({ providedIn: 'root' })
export class UserMonitoringService {
  private isActive = false;
  private readonly window;

  constructor(
    @Inject(SESSION_STORAGE) private readonly sessionStorage: Storage,
    private readonly logService: LogService,
    private readonly userMonitoringServiceConfig: UserMonitoringServiceConfig,
    windowService: WindowService,
  ) {
    this.window = windowService.windowRef();
  }

  bootstrap() {
    if (this.window && typeof dtrum !== 'undefined' && this.userMonitoringServiceConfig.userMonitoring.isEnabled) {
      this.isActive = true;
      this.logService.log('Dynatrace is initialized');
    } else {
      this.logService.log('Dynatrace was not initialized');
      return;
    }
    this.updateSessionContext({
      ...this.getCommonProperties(),
    });
  }

  // Add session context
  updateSessionContext(context: { [p: string]: string }): void {
    if (!this.isActive) {
      return;
    }
    Object.entries(context).forEach(([key, val]) => this.sessionStorage.setItem(`rum${key}`, val));
  }

  identifyUser(userName: string) {
    if (!this.isActive) {
      return;
    }
    dtrum.identifyUser(userName);
  }

  private getCommonProperties(): UserMonitoringCommonProperties {
    return {
      env: this.userMonitoringServiceConfig.userMonitoring.env,
      service: this.userMonitoringServiceConfig.userMonitoring.service,
      version: `${buildEnvironment.gitBranch}.${buildEnvironment.gitVersion}`,
    };
  }
}
